import { Capacitor } from "@capacitor/core";

import type { Rule, RulesEnforcementStatus, Target } from "../rules/ruleTypes";
import { NativeRules, type NativeRuleInput, type NativeSelectionSummary } from "./plugins";
import { getWebFilterStatus, webBlockingCapabilityFromStatus } from "./webFilter";

const unavailableStatus: RulesEnforcementStatus = {
  appBlocking: { status: "unavailable", reason: "web_or_plugin_unavailable", enforcedCount: 0 },
  webBlocking: { status: "entitlement_gated", reason: "network_extension_content_filter_entitlement_not_confirmed", enforcedCount: 0 },
};

function isNativeIOS(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

/** Both platforms enforce rules natively (iOS Screen Time, Android FGS + shield). */
function isNativeRulesPlatform(): boolean {
  return isNativeIOS() || isNativeAndroid();
}

/** Collect the picker selection ids a rule enforces (one per target that has a Screen Time selection). */
export function toNativeRule(rule: Rule, targetsById: Map<string, Target>): NativeRuleInput {
  const selectionIds = new Set<string>();
  for (const targetId of rule.targetIds) {
    const selection = targetsById.get(targetId)?.selection;
    if (selection?.id) selectionIds.add(selection.id);
  }
  return {
    id: rule._id,
    enabled: rule.enabled,
    days: rule.days,
    window: rule.window,
    selectionIds: [...selectionIds],
    repeats: rule.source !== "quick_session",
    ...(rule.suppressedUntil !== undefined ? { suppressedUntil: rule.suppressedUntil } : {}),
  };
}

export async function getNativeRulesStatus(): Promise<RulesEnforcementStatus> {
  if (!isNativeRulesPlatform()) return unavailableStatus;
  try {
    // App blocking comes from NativeRules (Screen Time); web blocking comes from
    // the separate NativeWebFilter VPN plugin. Merge so the existing contract
    // reflects real state for both capabilities.
    const [appStatus, webFilterStatus] = await Promise.all([
      NativeRules.getStatus(),
      getWebFilterStatus(),
    ]);
    return {
      appBlocking: appStatus.appBlocking,
      webBlocking: webBlockingCapabilityFromStatus(webFilterStatus),
    };
  } catch (error) {
    console.error("NativeRules.getStatus failed", error);
    return unavailableStatus;
  }
}

export async function applyNativeRules(rules: Rule[], targets: Target[]): Promise<RulesEnforcementStatus> {
  if (!isNativeRulesPlatform()) return unavailableStatus;
  const targetsById = new Map(targets.map((target) => [target._id, target]));
  try {
    return await NativeRules.applyRules({ rules: rules.map((rule) => toNativeRule(rule, targetsById)) });
  } catch (error) {
    console.error("NativeRules.applyRules failed", error);
    return unavailableStatus;
  }
}

const EMPTY_SUMMARY = (selectionId: string): NativeSelectionSummary => ({
  selectionId,
  applicationCount: 0,
  categoryCount: 0,
  webDomainCount: 0,
});

/** True only where the native FamilyActivityPicker exists (iOS native shell). */
export function canPickScreenTimeApps(): boolean {
  return isNativeIOS();
}

/** True where the native Android package picker exists (Android native shell). */
export function canPickAndroidApps(): boolean {
  return isNativeAndroid();
}

/** True on either native picker platform — use to gate the "choose apps" affordance. */
export function canPickBlockableApps(): boolean {
  return isNativeRulesPlatform();
}

export type AndroidPickerResult = {
  canceled: boolean;
  selectionId: string;
  applicationCount: number;
  apps: Array<{ packageName: string; displayName: string }>;
};

/**
 * Android package picker: returns the chosen apps (real package names + labels) and
 * persists them natively under `selectionId` so enforcement can resolve the packages.
 * Unlike iOS, there is no opaque-token/honor-system model — the caller can store the
 * returned apps directly into the target and show real icons immediately.
 */
export async function presentAndroidAppPicker(
  selectionId: string,
  options: AppPickerOptions = {},
): Promise<AndroidPickerResult> {
  const empty: AndroidPickerResult = { canceled: true, selectionId, applicationCount: 0, apps: [] };
  if (!isNativeAndroid()) return empty;
  try {
    const result = await NativeRules.presentAppPicker({ selectionId, ...options });
    return {
      canceled: result.canceled,
      selectionId,
      applicationCount: result.applicationCount,
      apps: result.apps ?? [],
    };
  } catch (error) {
    console.error("NativeRules.presentAppPicker (android) failed", error);
    return empty;
  }
}

/**
 * Android-only: persist a known package set as a selection without the picker (Smart
 * Block, where the target package is already known from usage data).
 */
export async function persistAndroidSelection(
  selectionId: string,
  packageNames: string[],
): Promise<NativeSelectionSummary> {
  if (!isNativeAndroid() || !NativeRules.setSelectionPackages) return EMPTY_SUMMARY(selectionId);
  try {
    return await NativeRules.setSelectionPackages({ selectionId, packageNames });
  } catch (error) {
    console.error("NativeRules.setSelectionPackages failed", error);
    return EMPTY_SUMMARY(selectionId);
  }
}

/** Android-only: open the "Display over other apps" settings screen for shield enforcement. */
export async function openAndroidOverlaySettings(): Promise<void> {
  if (!isNativeAndroid() || !NativeRules.openOverlaySettings) return;
  try {
    await NativeRules.openOverlaySettings();
  } catch (error) {
    console.error("NativeRules.openOverlaySettings failed", error);
  }
}

export type AppPickerOptions = {
  title?: string;
  headerText?: string;
  footerText?: string;
};

/**
 * Present the native FamilyActivityPicker for `selectionId` and persist the chosen
 * apps/categories/sites on-device. Returns `canceled: true` (and zero counts) on web
 * or if the user dismisses without choosing.
 *
 * Optional `headerText` / `footerText` / `title` are shown when launching from Smart
 * Block on iOS; Rules target editor omits them for default picker chrome.
 */
export async function presentAppPicker(
  selectionId: string,
  options: AppPickerOptions = {},
): Promise<NativeSelectionSummary & { canceled: boolean }> {
  if (!isNativeIOS()) return { ...EMPTY_SUMMARY(selectionId), canceled: true };
  try {
    return await NativeRules.presentAppPicker({ selectionId, ...options });
  } catch (error) {
    console.error("NativeRules.presentAppPicker failed", error);
    return { ...EMPTY_SUMMARY(selectionId), canceled: true };
  }
}

/**
 * Open the native sheet showing the picker selection's full list (real Apple icons/names).
 */
export async function presentSelectionDisplay(selectionId: string): Promise<{ presented: boolean; reason?: string }> {
  if (!isNativeIOS()) return { presented: false, reason: "not_native_ios" };
  try {
    return await NativeRules.presentSelectionDisplay({ selectionId });
  } catch (error) {
    console.error("NativeRules.presentSelectionDisplay failed", error);
    return { presented: false, reason: "native_error" };
  }
}

export async function presentCombinedSelectionDisplay(
  selectionIds: string[],
): Promise<{ presented: boolean; reason?: string }> {
  const uniqueIds = [...new Set(selectionIds.filter(Boolean))];
  if (uniqueIds.length === 0) return { presented: false, reason: "no_selection_ids" };
  if (!isNativeIOS()) return { presented: false, reason: "not_native_ios" };
  if (uniqueIds.length === 1) return presentSelectionDisplay(uniqueIds[0]!);
  try {
    return await NativeRules.presentCombinedSelectionDisplay({ selectionIds: uniqueIds });
  } catch (error) {
    console.error("NativeRules.presentCombinedSelectionDisplay failed", error);
    return { presented: false, reason: "native_error" };
  }
}

export async function getSelectionSummary(selectionId: string): Promise<NativeSelectionSummary> {
  if (!isNativeIOS()) return EMPTY_SUMMARY(selectionId);
  try {
    return await NativeRules.getSelectionSummary({ selectionId });
  } catch (error) {
    console.error("NativeRules.getSelectionSummary failed", error);
    return EMPTY_SUMMARY(selectionId);
  }
}
